import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { COUNTRIES, getCurrencyForCountry, getCitiesForCountry } from '../data/countries';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ChefHat, TrendingUp, Star, BarChart3, Eye, EyeOff, Check, Clock, Crown, MapPin, Globe, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';

const PLAN_ACCENTS = {
  base_single:    { border: 'border-stone-300', bg: 'bg-white',    accent: 'text-stone-600',  badge: 'bg-stone-100 text-stone-700',  ring: 'ring-stone-400' },
  pro_single:     { border: 'border-blue-400',  bg: 'bg-white',    accent: 'text-blue-600',   badge: 'bg-blue-50 text-blue-700',     ring: 'ring-blue-500' },
  premium_single: { border: 'border-amber-400', bg: 'bg-white',    accent: 'text-amber-700',  badge: 'bg-amber-50 text-amber-800',   ring: 'ring-amber-500' },
  base_multi:     { border: 'border-stone-300', bg: 'bg-white',    accent: 'text-stone-600',  badge: 'bg-stone-100 text-stone-700',  ring: 'ring-stone-400' },
  pro_multi:      { border: 'border-blue-400',  bg: 'bg-white',    accent: 'text-blue-600',   badge: 'bg-blue-50 text-blue-700',     ring: 'ring-blue-500' },
  premium_multi:  { border: 'border-purple-400',bg: 'bg-white',    accent: 'text-purple-700', badge: 'bg-purple-50 text-purple-800', ring: 'ring-purple-500' },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const { t, lang } = useLanguage();
  const defaultTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regStep, setRegStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(null);
  const [reg, setReg] = useState({
    name: '', email: '', password: '', company_name: '',
    account_type: 'single', plan: '',
    restaurant_name: '', restaurant_address: '', restaurant_city: '', restaurant_cuisine: '',
    restaurant_country: '', currency_symbol: '',
  });

  const availableCities = useMemo(() => {
    if (!reg.restaurant_country) return [];
    return getCitiesForCountry(reg.restaurant_country);
  }, [reg.restaurant_country]);

  const detectedCurrency = useMemo(() => {
    if (!reg.restaurant_country) return '';
    return getCurrencyForCountry(reg.restaurant_country);
  }, [reg.restaurant_country]);

  const it = lang === 'it';
  const tx = (itT, enT, thT) => lang === 'it' ? itT : lang === 'th' ? (thT || enT) : enT;

  useEffect(() => {
    api.getPublicPlans().then(data => {
      const plansList = data?.plans || data;
      if (Array.isArray(plansList) && plansList.length > 0) {
        setPlans(plansList);
      }
      if (data?.trial_days) setTrialDays(data.trial_days);
    }).catch(() => { if (!trialDays) setTrialDays(90); });
  }, []);

  const filteredPlans = useMemo(() => {
    return plans.filter(p => (p.category || 'single') === (reg.account_type || 'single'));
  }, [plans, reg.account_type]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await login(loginEmail, loginPassword);
      toast.success(t('save'));
      navigate(userData.role === 'superadmin' ? '/superadmin' : '/dashboard');
    }
    catch (error) { toast.error(error.response?.data?.detail || t('login_error')); }
    finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regStep === 1) {
      if (!reg.name || !reg.email || !reg.password) {
        toast.error(it ? 'Compila tutti i campi obbligatori' : (lang === 'th' ? 'กรุณากรอกข้อมูลที่จำเป็น' : 'Fill all required fields'));
        return;
      }
      if (reg.password.length < 6) {
        toast.error(it ? 'Password minimo 6 caratteri' : (lang === 'th' ? 'รหัสผ่านขั้นต่ำ 6 ตัวอักษร' : 'Password min 6 characters'));
        return;
      }
      setRegStep(2);
      return;
    }
    if (regStep === 2) {
      if (!reg.restaurant_name) {
        toast.error(it ? 'Inserisci il nome del ristorante' : (lang === 'th' ? 'กรุณาใส่ชื่อร้านอาหาร' : 'Enter restaurant name'));
        return;
      }
      setRegStep(3);
      return;
    }
    if (!reg.plan) {
      toast.error(it ? 'Seleziona un piano per continuare' : (lang === 'th' ? 'เลือกแผนเพื่อดำเนินการต่อ' : 'Select a plan to continue'));
      return;
    }
    setIsLoading(true);
    try {
      await register(reg);
      toast.success(it
        ? `Registrazione completata! Trial gratuito di ${trialDays || 90} giorni attivo.`
        : `Registration complete! ${trialDays || 90}-day free trial active.`);
      navigate('/dashboard');
    }
    catch (error) { toast.error(error.response?.data?.detail || t('register_error')); }
    finally { setIsLoading(false); }
  };

  // Step 3: Full-page plan selection
  if (regStep === 3) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="plan-selection-page">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2.5 mb-4">
              <img src="/images/logo-icon.png" alt="" className="h-12 w-auto" />
              <span className="font-outfit text-xl font-bold text-[#1C1917]">
                Risto<span className="text-[#6BBE44]">Metrix</span>
              </span>
            </div>
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-[#1C1917] mb-2">
              {it ? 'Scegli il piano perfetto per te' : (lang === 'th' ? 'เลือกแผนที่เหมาะกับคุณ' : 'Choose the perfect plan for you')}
            </h2>
            <p className="text-[#78716C] text-sm sm:text-base max-w-xl mx-auto">
              {it
                ? `Ogni piano include un trial gratuito di ${trialDays || 90} giorni. Nessun impegno, cancella quando vuoi.`
                : (lang === 'th' ? `แผนทุกแผนรวมทดลองฟรี ${trialDays || 90} วัน ไม่มีข้อผูกมัด ยกเลิกได้ตลอดเวลา`
                : `Every plan includes a ${trialDays || 90}-day free trial. No commitment, cancel anytime.`)}
            </p>
          </div>

          {/* Account type toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-white border border-stone-200 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setReg(r => ({...r, account_type: 'single', plan: ''}))}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  reg.account_type === 'single' ? 'bg-[#2E4A3D] text-white shadow-sm' : 'text-[#78716C] hover:text-[#1C1917]'
                }`}
                data-testid="toggle-single"
              >
                {it ? 'Singolo ristorante' : (lang === 'th' ? 'ร้านเดียว' : 'Single restaurant')}
              </button>
              <button
                type="button"
                onClick={() => setReg(r => ({...r, account_type: 'multi', plan: ''}))}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  reg.account_type === 'multi' ? 'bg-[#2E4A3D] text-white shadow-sm' : 'text-[#78716C] hover:text-[#1C1917]'
                }`}
                data-testid="toggle-multi"
              >
                {it ? 'Multi-ristorante' : (lang === 'th' ? 'หลายร้าน' : 'Multi-restaurant')}
              </button>
            </div>
          </div>

          {/* Plans grid */}
          <div className={`grid gap-5 mb-8 ${filteredPlans.length >= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'}`}>
            {filteredPlans.map(p => {
              const style = PLAN_ACCENTS[p.id] || PLAN_ACCENTS.base_single;
              const name = it ? (p.name_it || p.id) : (lang === 'th' ? (p.name_th || p.name_en || p.id) : (p.name_en || p.id));
              const desc = it ? (p.description_it || '') : (lang === 'th' ? (p.description_th || p.description_en || '') : (p.description_en || ''));
              const features = it ? (p.features_labels_it || []) : (lang === 'th' ? (p.features_labels_th || p.features_labels_en || []) : (p.features_labels_en || []));
              const isSelected = reg.plan === p.id;
              const isPopular = p.is_popular;

              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setReg(r => ({...r, plan: p.id}))}
                  className={`relative text-left rounded-xl border-2 p-5 sm:p-6 transition-all duration-200 ${style.bg} ${
                    isSelected
                      ? `${style.border} ${style.ring} ring-2 ring-offset-2 shadow-lg scale-[1.02]`
                      : 'border-stone-200 hover:border-stone-300 hover:shadow-md'
                  }`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#2E4A3D] text-white text-xs px-3 py-0.5 shadow-sm">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {it ? 'Consigliato' : (lang === 'th' ? 'แนะนำ' : 'Recommended')}
                      </Badge>
                    </div>
                  )}

                  {/* Plan header */}
                  <div className="mb-4">
                    <h3 className={`font-outfit text-lg font-bold ${style.accent}`}>{name}</h3>
                    {desc && <p className="text-xs text-[#78716C] mt-1">{desc}</p>}
                  </div>

                  {/* Price */}
                  {p.price && (
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-[#1C1917]">{p.price}</span>
                      <span className="text-sm text-[#78716C] ml-1">/{it ? 'mese' : (lang === 'th' ? 'เดือน' : 'mo')}</span>
                    </div>
                  )}

                  {/* All features */}
                  <ul className="space-y-2">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#44403C]">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-[#2E4A3D]' : 'text-emerald-500'}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="mt-4 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#2E4A3D]/10">
                      <Check className="w-4 h-4 text-[#2E4A3D]" />
                      <span className="text-sm font-medium text-[#2E4A3D]">{it ? 'Selezionato' : (lang === 'th' ? 'เลือกแล้ว' : 'Selected')}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Trial + max restaurants info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <Clock className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                {it ? `Trial gratuito di ${trialDays || 90} giorni` : (lang === 'th' ? `ทดลองฟรี ${trialDays || 90} วัน` : `${trialDays || 90}-day free trial`)}
              </span>
            </div>
            {reg.plan && (() => {
              const sel = filteredPlans.find(p => p.id === reg.plan);
              return sel?.max_restaurants ? (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-50 border border-blue-200">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    {it ? `Fino a ${sel.max_restaurants} ristoranti` : (lang === 'th' ? `สูงสุด ${sel.max_restaurants} ร้านอาหาร` : `Up to ${sel.max_restaurants} restaurants`)}
                  </span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3 max-w-md mx-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRegStep(2)}
              className="flex-1"
              data-testid="plan-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {it ? 'Indietro' : (lang === 'th' ? 'กลับ' : 'Back')}
            </Button>
            <Button
              type="button"
              onClick={handleRegister}
              disabled={!reg.plan || isLoading}
              className="flex-1 bg-[#2E4A3D] hover:bg-[#23392F] text-white"
              data-testid="plan-confirm-btn"
            >
              {isLoading
                ? (tx('Registrazione...', 'Registering...', 'กำลังลงทะเบียน...'))
                : (it ? 'Inizia il trial' : (lang === 'th' ? 'เริ่มทดลอง' : 'Start trial'))}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Steps 1 & 2: Login / Register form
  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Login — RistoMetrix</title>
      </Helmet>
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAF9]">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <img src="/images/logo-icon.png" alt="" className="h-14 w-auto" />
            <span className="font-outfit text-2xl font-bold text-[#1C1917]">
              Risto<span className="text-[#6BBE44]">Metrix</span>
            </span>
          </div>

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">{t('login')}</TabsTrigger>
              <TabsTrigger value="register" onClick={() => setRegStep(1)} data-testid="register-tab">{tx('Registrati', 'Register', 'ลงทะเบียน')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-outfit">{t('welcome_back')}</CardTitle>
                  <CardDescription>{t('login_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t('email_label')}</Label>
                      <Input id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required data-testid="login-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t('password_label')}</Label>
                      <div className="relative">
                        <Input id="login-password" type={showLoginPw ? 'text' : 'password'} placeholder="--------" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required data-testid="login-password-input" />
                        <button type="button" onClick={() => setShowLoginPw(!showLoginPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917] transition-colors" data-testid="toggle-login-password">
                          {showLoginPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#2E4A3D] hover:bg-[#23392F] text-white" disabled={isLoading} data-testid="login-submit-btn">
                      {isLoading ? t('logging_in') : t('login')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-outfit">
                    {regStep === 1
                      ? (it ? 'Crea il tuo account' : (lang === 'th' ? 'สร้างบัญชีของคุณ' : 'Create your account'))
                      : (it ? 'Il tuo ristorante' : (lang === 'th' ? 'ร้านอาหารของคุณ' : 'Your restaurant'))}
                  </CardTitle>
                  <CardDescription>
                    {regStep === 1
                      ? (it ? 'Passo 1 di 3 — Dati personali' : (lang === 'th' ? 'ขั้นตอน 1 จาก 3 — ข้อมูลส่วนตัว' : 'Step 1 of 3 — Personal info'))
                      : (it ? 'Passo 2 di 3 — Info ristorante' : (lang === 'th' ? 'ขั้นตอน 2 จาก 3 — ข้อมูลร้านอาหาร' : 'Step 2 of 3 — Restaurant info'))}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    {regStep === 1 ? (
                      <>
                        <div className="space-y-2">
                          <Label>{tx('Nome completo', 'Full name', 'ชื่อเต็ม')} *</Label>
                          <Input value={reg.name} onChange={e => setReg({...reg, name: e.target.value})} required data-testid="register-name-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" value={reg.email} onChange={e => setReg({...reg, email: e.target.value})} required data-testid="register-email-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>{it ? 'Nome azienda' : (lang === 'th' ? 'ชื่อบริษัท' : 'Company name')}</Label>
                          <Input value={reg.company_name} onChange={e => setReg({...reg, company_name: e.target.value})} data-testid="register-company-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Password *</Label>
                          <div className="relative">
                            <Input type={showRegPw ? 'text' : 'password'} placeholder="--------" value={reg.password} onChange={e => setReg({...reg, password: e.target.value})} required data-testid="register-password-input" />
                            <button type="button" onClick={() => setShowRegPw(!showRegPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]" data-testid="toggle-register-password">
                              {showRegPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-[#2E4A3D] hover:bg-[#23392F] text-white" data-testid="register-next-btn">
                          {it ? 'Avanti' : (lang === 'th' ? 'ถัดไป' : 'Next')} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2 col-span-2">
                            <Label>{it ? 'Nome ristorante' : (lang === 'th' ? 'ชื่อร้านอาหาร' : 'Restaurant name')} *</Label>
                            <Input value={reg.restaurant_name} onChange={e => setReg({...reg, restaurant_name: e.target.value})} placeholder={it ? 'Es. Osteria del Mare' : (lang === 'th' ? 'เช่น ร้านอาหารไทย' : 'E.g. The Kitchen')} required data-testid="register-restaurant-name" />
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1.5">
                              <Globe className="w-3.5 h-3.5 text-[#2E4A3D]" />
                              {it ? 'Nazione' : (lang === 'th' ? 'ประเทศ' : 'Country')}
                            </Label>
                            <select
                              value={reg.restaurant_country}
                              onChange={e => {
                                const country = e.target.value;
                                const currency = getCurrencyForCountry(country);
                                setReg({...reg, restaurant_country: country, restaurant_city: '', currency_symbol: currency});
                              }}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              data-testid="register-restaurant-country"
                            >
                              <option value="">{it ? 'Seleziona nazione...' : (lang === 'th' ? 'เลือกประเทศ...' : 'Select country...')}</option>
                              {COUNTRIES.map(c => (
                                <option key={c.code} value={it ? c.name_it : c.name_en}>{it ? c.name_it : c.name_en}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-[#2E4A3D]" />
                              {it ? 'Citta' : (lang === 'th' ? 'เมือง' : 'City')}
                            </Label>
                            {availableCities.length > 0 ? (
                              <select
                                value={reg.restaurant_city}
                                onChange={e => setReg({...reg, restaurant_city: e.target.value})}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                data-testid="register-restaurant-city"
                              >
                                <option value="">{it ? 'Seleziona citta...' : (lang === 'th' ? 'เลือกเมือง...' : 'Select city...')}</option>
                                {availableCities.map(city => (
                                  <option key={city} value={city}>{city}</option>
                                ))}
                              </select>
                            ) : (
                              <Input value={reg.restaurant_city} onChange={e => setReg({...reg, restaurant_city: e.target.value})} placeholder={it ? 'Inserisci citta' : (lang === 'th' ? 'ใส่เมือง' : 'Enter city')} data-testid="register-restaurant-city" />
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>{it ? 'Tipo cucina' : (lang === 'th' ? 'ประเภทอาหาร' : 'Cuisine')}</Label>
                            <Input value={reg.restaurant_cuisine} onChange={e => setReg({...reg, restaurant_cuisine: e.target.value})} placeholder={it ? 'Italiana, Sushi...' : (lang === 'th' ? 'ไทย, ซูชิ...' : 'Italian, Sushi...')} data-testid="register-restaurant-cuisine" />
                          </div>
                          <div className="space-y-2">
                            <Label>{it ? 'Indirizzo' : (lang === 'th' ? 'ที่อยู่' : 'Address')}</Label>
                            <Input value={reg.restaurant_address} onChange={e => setReg({...reg, restaurant_address: e.target.value})} data-testid="register-restaurant-address" />
                          </div>
                        </div>

                        {detectedCurrency && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                            <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <p className="text-sm text-blue-700">
                              {it ? 'Valuta rilevata automaticamente:' : (lang === 'th' ? 'สกุลเงินที่ตรวจพบอัตโนมัติ:' : 'Auto-detected currency:')}{' '}
                              <span className="font-semibold">{detectedCurrency}</span>
                            </p>
                          </div>
                        )}

                        {/* Trial info */}
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                          <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-emerald-800">
                              {it ? `Trial gratuito di ${trialDays || 90} giorni` : (lang === 'th' ? `ทดลองฟรี ${trialDays || 90} วัน` : `${trialDays || 90}-day free trial`)}
                            </p>
                            <p className="text-xs text-emerald-600">
                              {it ? 'Prova tutte le funzionalita senza impegno' : (lang === 'th' ? 'ทดลองทุกฟีเจอร์โดยไม่มีข้อผูกมัด' : 'Try all features with no commitment')}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={() => setRegStep(1)} className="flex-1" data-testid="register-back-btn">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {it ? 'Indietro' : (lang === 'th' ? 'กลับ' : 'Back')}
                          </Button>
                          <Button type="submit" className="flex-1 bg-[#2E4A3D] hover:bg-[#23392F] text-white" data-testid="register-next-step3-btn">
                            {it ? 'Scegli il piano' : (lang === 'th' ? 'เลือกแผน' : 'Choose plan')} <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </>
                    )}
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div className="hidden lg:block lg:flex-1 relative">
        <img src="https://images.unsplash.com/photo-1768697359488-9cc9937056a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxmaW5lJTIwZGluaW5nJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwZWxlZ2FudHxlbnwwfHx8fDE3NzMzMDgyOTB8MA&ixlib=rb-4.1.0&q=85" alt="Restaurant" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h2 className="font-outfit text-3xl font-semibold text-white mb-4">{t('hero_title')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 text-white/90"><TrendingUp className="w-5 h-5 text-[#D4A373]" /><span className="text-sm">{t('hero_revenue')}</span></div>
            <div className="flex items-center gap-3 text-white/90"><Star className="w-5 h-5 text-[#D4A373]" /><span className="text-sm">{t('hero_reviews')}</span></div>
            <div className="flex items-center gap-3 text-white/90"><BarChart3 className="w-5 h-5 text-[#D4A373]" /><span className="text-sm">{t('hero_multi')}</span></div>
            <div className="flex items-center gap-3 text-white/90"><ChefHat className="w-5 h-5 text-[#D4A373]" /><span className="text-sm">{t('hero_ai')}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
